import React from 'react';
import type { CredentialSettings, ProfileSettings, SessionInfo } from './settingsTypes';

type PasswordFormState = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type PasswordFieldErrors = {
  newPassword?: string;
  confirmPassword?: string;
  submit?: string;
};

type SecuritySettingsTabProps = {
  profile: ProfileSettings;
  credentials: CredentialSettings | null;
  session: SessionInfo | null;
  profileLoading: boolean;
  passwordForm: PasswordFormState;
  passwordErrors: PasswordFieldErrors;
  passwordSaving: boolean;
  passwordSuccess: boolean;
  googleDisconnecting: boolean;
  googleDisconnectConfirm: boolean;
  forcePasswordChange?: boolean;
  onDismissForceBanner?: () => void;
  onProfileChange: (patch: Partial<ProfileSettings>) => void;
  onAvatarUpload: (file: File) => Promise<void>;
  onRemoveAvatar: () => void;
  avatarError: string | null;
  onPasswordFieldChange: (field: keyof PasswordFormState, value: string) => void;
  onUpdatePassword: () => void;
  onSignOutOthers: () => void;
  onConnectGoogle: () => void;
  onRequestDisconnectGoogle: () => void;
  onCancelDisconnectGoogle: () => void;
  onConfirmDisconnectGoogle: () => void;
};

function initialsFromName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed.charAt(0).toUpperCase();
}

export default function SecuritySettingsTab({
  profile,
  credentials,
  session,
  profileLoading,
  passwordForm,
  passwordErrors,
  passwordSaving,
  passwordSuccess,
  onProfileChange,
  onAvatarUpload,
  onRemoveAvatar,
  avatarError,
  onPasswordFieldChange,
  onUpdatePassword,
  onSignOutOthers,
  googleDisconnecting,
  googleDisconnectConfirm,
  forcePasswordChange = false,
  onDismissForceBanner,
  onConnectGoogle,
  onRequestDisconnectGoogle,
  onCancelDisconnectGoogle,
  onConfirmDisconnectGoogle
}: SecuritySettingsTabProps): React.JSX.Element {
  return (
    <div className="settings-tab-stack">
      {forcePasswordChange ? (
        <div className="settings-force-banner">
          <p>Please change your temporary password before continuing.</p>
          <button
            type="button"
            className="settings-force-banner-dismiss"
            aria-label="Dismiss banner"
            onClick={onDismissForceBanner}
          >
            ×
          </button>
        </div>
      ) : null}

      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Profile &amp; display</div>
        <p className="settings-hint">How your name and avatar appear across the workspace.</p>

        <div className="settings-profile-row">
          <div className="settings-avatar-wrap">
            {profile.avatarBase64 ? (
              <img src={profile.avatarBase64} alt="" className="settings-avatar-image" />
            ) : (
              <div className="settings-avatar-initials" aria-hidden>
                {initialsFromName(profile.displayName)}
              </div>
            )}
          </div>
          <div className="settings-avatar-actions">
            <label className="btn-secondary settings-logo-upload-btn">
              Upload photo
              <input
                type="file"
                accept="image/png,image/jpeg"
                disabled={profileLoading}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  await onAvatarUpload(file);
                }}
              />
            </label>
            {profile.avatarBase64 ? (
              <button type="button" className="settings-logo-remove-btn" onClick={onRemoveAvatar}>
                Remove photo
              </button>
            ) : null}
            <p className="settings-hint settings-logo-hint">PNG or JPG, max 2 MB.</p>
            {avatarError ? <p className="settings-field-error">{avatarError}</p> : null}
          </div>
        </div>

        <div className="form-group" style={{ marginTop: 18 }}>
          <label className="form-label" htmlFor="settings-display-name">
            Display name
          </label>
          <input
            id="settings-display-name"
            className="form-input"
            value={profile.displayName}
            disabled={profileLoading}
            onChange={(e) => onProfileChange({ displayName: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Role</label>
          <span className="settings-role-badge">Admin</span>
        </div>
      </section>

      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Login credentials</div>
        <p className="settings-hint">Your sign-in identity for this workspace.</p>

        <div className="form-group">
          <label className="form-label" htmlFor="settings-email">
            Email address
          </label>
          <input
            id="settings-email"
            className="form-input settings-input-readonly"
            value={credentials?.email || ''}
            readOnly
            disabled
          />
          <p className="settings-field-helper">Contact support to change your login email.</p>
        </div>

        <div className="form-group">
          <label className="form-label">Google account</label>
          {credentials?.googleConnected ? (
            <div className="settings-google-connected">
              <span className="settings-status-badge settings-status-badge--connected">Connected</span>
              {credentials.googleEmail ? (
                <p className="settings-field-helper settings-google-email">{credentials.googleEmail}</p>
              ) : null}
              {!googleDisconnectConfirm ? (
                <span
                  className="settings-tooltip-wrap"
                  title={
                    credentials.hasPassword
                      ? undefined
                      : 'Set a password before disconnecting Google.'
                  }
                >
                  <button
                    type="button"
                    className="settings-text-btn"
                    disabled={!credentials.hasPassword || googleDisconnecting}
                    onClick={onRequestDisconnectGoogle}
                  >
                    Disconnect
                  </button>
                </span>
              ) : (
                <div className="settings-inline-confirm">
                  <p className="settings-inline-confirm-text">
                    You&apos;ll need to use your password to sign in. Disconnect?
                  </p>
                  <div className="settings-inline-confirm-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={googleDisconnecting}
                      onClick={onConfirmDisconnectGoogle}
                    >
                      {googleDisconnecting ? 'Disconnecting…' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      className="settings-text-btn"
                      disabled={googleDisconnecting}
                      onClick={onCancelDisconnectGoogle}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="settings-google-disconnected">
              <span className="settings-status-muted">Not connected</span>
              <button
                type="button"
                className="btn-secondary settings-inline-action"
                disabled={profileLoading}
                onClick={onConnectGoogle}
              >
                Connect Google account
              </button>
            </div>
          )}
        </div>
      </section>

      <section
        id="settings-password-card"
        className={`quote-card settings-section-card settings-password-card${
          forcePasswordChange ? ' is-highlighted' : ''
        }`}
      >
        <div className="quote-section-title">Password</div>

        {credentials && !credentials.hasPassword ? (
          <p className="settings-placeholder-text">
            Your account uses Google Sign-In. Password login is not enabled.
          </p>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label" htmlFor="settings-current-password">
                Current password
              </label>
              <input
                id="settings-current-password"
                className="form-input"
                type="password"
                autoComplete="current-password"
                value={passwordForm.currentPassword}
                onChange={(e) => onPasswordFieldChange('currentPassword', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="settings-new-password">
                New password
              </label>
              <input
                id="settings-new-password"
                className="form-input"
                type="password"
                autoComplete="new-password"
                value={passwordForm.newPassword}
                onChange={(e) => onPasswordFieldChange('newPassword', e.target.value)}
              />
              {passwordErrors.newPassword ? (
                <p className="settings-field-error">{passwordErrors.newPassword}</p>
              ) : (
                <p className="settings-field-helper">
                  Minimum 8 characters, one uppercase letter, and one number or special character.
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="settings-confirm-password">
                Confirm new password
              </label>
              <input
                id="settings-confirm-password"
                className="form-input"
                type="password"
                autoComplete="new-password"
                value={passwordForm.confirmPassword}
                onChange={(e) => onPasswordFieldChange('confirmPassword', e.target.value)}
              />
              {passwordErrors.confirmPassword ? (
                <p className="settings-field-error">{passwordErrors.confirmPassword}</p>
              ) : null}
            </div>

            {passwordErrors.submit ? <p className="settings-field-error">{passwordErrors.submit}</p> : null}
            {passwordSuccess ? <p className="settings-field-success">Password updated.</p> : null}

            <button
              type="button"
              className="btn-secondary settings-inline-action"
              disabled={passwordSaving}
              onClick={onUpdatePassword}
            >
              {passwordSaving ? 'Updating…' : 'Update password'}
            </button>
          </>
        )}
      </section>

      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Active sessions</div>
        <p className="settings-hint">Devices currently signed in to your account.</p>

        <div className="settings-session-card">
          <div className="settings-session-row">
            <span className="settings-session-label">{session?.label || 'Current session'}</span>
            <span className="settings-session-meta">
              {session ? `${session.browser} · ${session.os}` : '—'}
            </span>
          </div>
          <div className="settings-session-row">
            <span className="settings-session-sub">Last active</span>
            <span className="settings-session-meta">
              {session?.lastActive
                ? new Date(session.lastActive).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })
                : '—'}
            </span>
          </div>
        </div>

        <button type="button" className="btn-secondary settings-inline-action" onClick={onSignOutOthers}>
          Sign out all other sessions
        </button>
      </section>
    </div>
  );
}
